import type { getCart } from "@fluid-app/fluid";

/**
 * Format a price with consistent currency display
 * @param price The price string to format
 * @returns A formatted price string
 */
export const formatDisplayPrice = (price: string | undefined): string => {
  if (!price) return "$0.00";

  // If price already includes currency code in parentheses, return as is
  if (price.includes("(") && price.includes(")")) {
    // Remove the currency code in parentheses
    return price.replace(/\s*\([^)]*\)/, "");
  }

  // Extract the currency symbol if present
  const symbolRegex = /^[^0-9]*/;
  const symbolMatch = symbolRegex.exec(price);
  const currencySymbol = symbolMatch?.[0] ?? "$";

  // Extract numeric value
  const priceRegex = /[0-9]+\.?[0-9]*/;
  const numericMatch = priceRegex.exec(price);
  const amount = numericMatch ? parseFloat(numericMatch[0]) : 0;

  // Format without currency code
  return `${currencySymbol}${amount.toFixed(2)}`;
};

/**
 * Calculate total item count in a cart
 * @param cartData The cart data
 * @returns The total number of items
 */
export const calculateTotalItems = (
  cartData: Awaited<ReturnType<typeof getCart>> | null,
): number => {
  if (!cartData?.items) return 0;

  return cartData.items.reduce((total, item) => {
    return total + (Number(item.quantity) || 0);
  }, 0);
};

/**
 * Calculate estimated cart totals with the given items
 * This is used for optimistic updates before API responses
 * @param currentCart The current cart
 * @param updatedItems The updated items
 * @returns A new cart object with updated totals
 */
export const calculateEstimatedTotals = (
  currentCart: Awaited<ReturnType<typeof getCart>>,
  updatedItems: NonNullable<Awaited<ReturnType<typeof getCart>>>["items"],
): Awaited<ReturnType<typeof getCart>> => {
  // If no current cart or items, return as is
  if (!currentCart?.items || !updatedItems) return currentCart;

  // Extract currency information - explicitly use empty string to avoid null values
  const currencySymbol = currentCart.currency_symbol ?? "$";
  const currencyCode = currentCart.currency_code ?? "USD";

  // Calculate new sub total using price_in_currency values
  const subTotal = updatedItems.reduce((total, item) => {
    // Use subscription price if item is subscribed, otherwise use regular price
    const priceField =
      item.subscription && item.subscription_price_in_currency
        ? item.subscription_price_in_currency
        : item.price_in_currency;

    // Extract numeric value from price field
    if (!priceField) return total;

    // Parse the numeric value from the currency string
    const priceRegex = /[0-9]+\.?[0-9]*/;
    const numericMatch = priceRegex.exec(priceField);
    const itemPrice = numericMatch ? parseFloat(numericMatch[0]) : 0;

    // Get quantity as a number
    const quantity = Number(item.quantity) || 0;

    return total + itemPrice * quantity;
  }, 0);

  // Format as price
  const formatPrice = (amount: number): string => {
    return amount.toFixed(2);
  };

  // Format with currency - ensure consistent format with the formatDisplayPrice function
  const formatWithCurrency = (amount: number): string => {
    return `${currencySymbol}${formatPrice(amount)} (${currencyCode})`;
  };

  // Parse shipping and discounts from in_currency values
  let shippingTotal = 0;
  if (currentCart.shipping_total_in_currency) {
    const shippingRegex = /[0-9]+\.?[0-9]*/;
    const shippingMatch = shippingRegex.exec(
      currentCart.shipping_total_in_currency,
    );
    shippingTotal = shippingMatch ? parseFloat(shippingMatch[0]) : 0;
  }

  let discountTotal = 0;
  if (currentCart.discount_total_in_currency) {
    const discountRegex = /[0-9]+\.?[0-9]*/;
    const discountMatch = discountRegex.exec(
      currentCart.discount_total_in_currency,
    );
    discountTotal = discountMatch ? parseFloat(discountMatch[0]) : 0;
  }

  // Calculate total
  const amountTotal = subTotal + shippingTotal - discountTotal;

  // Return a new cart object with updated totals
  return {
    ...currentCart,
    items: updatedItems,
    sub_total: formatPrice(subTotal),
    sub_total_in_currency: formatWithCurrency(subTotal),
    amount_total: formatPrice(amountTotal),
    amount_total_in_currency: formatWithCurrency(amountTotal),
  };
};

/**
 * Calculate and format the total subscription savings with quantity
 * @param savingsDisplay The original savings display string (e.g. "Subscribe and save $5.00")
 * @param quantity The item quantity
 * @returns A formatted string with total savings based on quantity
 */
export const calculateTotalSubscriptionSavings = (
  savingsDisplay: string | undefined | null,
  quantity: number,
): string => {
  if (!savingsDisplay || quantity <= 1) return savingsDisplay ?? "";

  // Extract the base prefix and the savings amount
  const regex = /^(.*\$)([\d,]+\.\d{2})(.*)$/;
  const matches = regex.exec(savingsDisplay);
  if (!matches || matches.length < 3) return savingsDisplay;

  const prefix = matches[1] ?? "";
  const amountStr = matches[2] ?? "0.00";
  const suffix = matches[3] ?? "";

  // Parse the savings amount
  const amount = parseFloat(amountStr.replace(/,/g, ""));

  // Calculate total savings based on quantity
  const totalSavings = amount * quantity;

  // Format the total savings with proper commas for thousands
  const formattedTotal = totalSavings
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Return the formatted string
  return `${prefix}${formattedTotal}${suffix}`;
};
